import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCampaignbyId } from "../../../api/Campaigns";
import { faArrowLeft, faHeart as faHeartSolid, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { faHeart } from "@fortawesome/free-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import { getNpInfo } from "../../../api/NonprofitInfo";
import Slider from "react-slick";
import { generateDonation } from "../../../api/GenerateDonation";
import { donationSuccess } from "../../../api/DonationSuccess";
import { parseDescription } from "../../shared/Utils/ParseDescription";
import SuccessModal from "./SuccessModal/SuccessModal";
import NotFound from "../../shared/Utils/NotFound";
import { useSelector } from "react-redux";
import LoginSignupModal from "../../shared/LoginSignupModal/LoginSignupModal";
import Spinner from "../../shared/LoadingSpinner/Spinner";
import classes from "./CampaignFeature.module.css";
import DonationBox from "../../shared/DonationBox/DonationBox";
import CampaignDonorsBlock from "../CampaignDonorsBlock/CampaignDonorsBlock";
import { extractMissionStatement } from "../../shared/Utils/ExtractMissionStatement";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import PrimaryButton from "../../shared/Buttons/PrimaryButton";
import { useDispatch } from "react-redux";
import { cartActions } from "../../../store/index";
import { formatDate } from "../../shared/Utils/DateFilters";

const ageOptions = {
  Newborn: 1,
  Young: 2,
  Adult: 3,
  Senior: 4,
  Unknown: 5,
};

// productTypeOptions commented out - shop feature disabled
// const productTypeOptions = {
//   All: "all",
//   Food: "food",
//   Toys: "toys",
//   Supplies: "supplies",
//   Beds: "beds",
//   "Medical Equipment": "medical",
//   Other: "other",
// };

const projectStatusOptions = {
  "All Statuses": "all",
  Planning: "planning",
  "Under Construction": "construction",
  Completed: "completed",
};

function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}

const DonationProgress = ({ campaign }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [shareHovered, setShareHovered] = useState(false);

  const progressPercentage = Math.min(
    (campaign.donation_amount / campaign.donation_goal) * 100,
    100
  ).toFixed(0);

  const handleShareClick = () => {
    const shareLink = window.location.href;
    navigator.clipboard.writeText(shareLink);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 3000);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold text-[30px]">Help Us Reach Our Goal</h2>
        <button
          onClick={handleShareClick}
          className="flex items-center gap-2 bg-orange rounded-full px-6 py-3 font-semibold text-base text-black"
          onMouseEnter={() => setShareHovered(true)}
          onMouseLeave={() => setShareHovered(false)}
        >
          <span className="text-white"><FontAwesomeIcon icon={shareHovered ? faHeartSolid : faHeart} /></span> Share
        </button>
      </div>
      {showTooltip && (
        <p className="text-green-500 text-sm font-semibold mb-2">
          Link copied to clipboard!
        </p>
      )}
      <p className="text-center font-bold text-xl mb-2">{progressPercentage}%</p>
      <div className="h-2 bg-gray-200 rounded-full mb-3">
        <div
          className="h-2 bg-blue rounded-full"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
      <div className="flex justify-between text-sm font-semibold mb-6">
        <span>
          Raised{" "}
          <strong>
            ${campaign.donation_amount ? campaign.donation_amount.toLocaleString() : "0"}
          </strong>
        </span>
        <span>
          Goal <strong>${campaign.donation_goal.toLocaleString()}</strong>
        </span>
      </div>
    </div>
  );
};

export default function CampaignFeature({
  isOpen,
  onClose,
  campaignData,
  imagePreviews,
  graphicImagePreviews,
  campaignType,
}) {
  const { nonprofit_name, animal_name, campaign_id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [nonprofit, setNonprofit] = useState(null);
  const [npStatement, setNpStatement] = useState([]);
  const [ifReadMore, setIfReadMore] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [donationAmount, setDonationAmount] = useState(0);
  const [donationId, setDonationId] = useState(0);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [localUserData, setLocalUserData] = useState(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [blurredImages, setBlurredImages] = useState({});
  const [error, setError] = useState(null);
  const [coverFee, setCoverFee] = useState(true);
  const [prevAmount, setPrevAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [showModal, setShowModal] = useState(false);
  const userData = useSelector((state) => state.userData.userData);

  // States for login/signup
  const [isLoginSignupModalOpen, setLoginSignupModalOpenModalOpen] =
    useState(false);
  const [loginOrSignup, setLoginOrSignup] = useState("");

  const [completedDonations, setCompletedDonations] = useState([]);

  // States for adding donation to cart
  const [cartError, setCartError] = useState("");
  const [showCartModal, setShowCartModal] = useState(false);

  // States for preview campaign feature
  const [ifPreview, setIfPreview] = useState(false);
  const [allImages, setAllImages] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [blurredPreviewImages, setBlurredPreviewImages] = useState({});

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Gets the type from the URLs
  // Requires using both due to animal campaigns having a different URL path
  const params = useParams();
  const location = useLocation();
  const type = location.pathname.includes("/animal/") ? "animal" : params.type;

  function terminalWarnings() {
    return isStripeLoading;
  }

  terminalWarnings();

  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      setLocalUserData(JSON.parse(userData));
    }
  }, [userData]);

  // Handles and sets state for the Preview Modal
  useEffect(() => {
    if (isOpen) {
      setIfPreview(true);

      if (campaignData?.images) {
        const images = [
          ...imagePreviews.map((image) => ({
            image,
            graphic: false,
            show: true,
          })),
          ...graphicImagePreviews.map((image) => ({
            image,
            graphic: true,
            show: blurredPreviewImages[image] || false,
          })),
        ];

        setAllImages(images);
      }

      if (campaignData?.video_urls) {
        const regex = new RegExp(/^(https?:\/\/)/);
        const validateUrls = (urls) => {
          if (urls) {
            let validatedUrls = urls.split(",");
            for (let i = 0; i < validatedUrls.length; i++) {
              validatedUrls[i] = validatedUrls[i].trim();
              if (regex.test(validatedUrls[i])) {
                if (
                  validatedUrls[i].includes("youtube") &&
                  validatedUrls[i].indexOf("watch?v=") !== -1
                ) {
                  validatedUrls[i] = validatedUrls[i].replace(
                    "watch?v=",
                    "embed/"
                  );
                  validatedUrls[i] = validatedUrls[i].slice(
                    0,
                    validatedUrls[i].indexOf("&")
                  );
                }
              } else {
                validatedUrls[i] = "URL not valid";
              }
            }
            setVideoUrls(validatedUrls);
          }
        };

        validateUrls(campaignData.video_urls);
      }
    }
  }, [isOpen, blurredPreviewImages]);

  useEffect(() => {
    if (campaign?.campaign_images) {
      campaign.campaign_images.forEach((image) => {
        if (image.graphic === true) {
          setBlurredImages((prevState) => ({
            ...prevState,
            [image.image_url]: true, // Set the initial value to true (blurred)
          }));
        }
      });
    }
  }, [campaign]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAll = async () => {
      try {
        const tempLoadedCampaign = await getCampaignbyId(campaign_id, type, { signal: controller.signal });

        if (!tempLoadedCampaign) {
          throw new Error("Campaign not found");
        }
        const tempLoadedNonprofit = await getNpInfo(
          tempLoadedCampaign.nonprofit
        );

        const donations = tempLoadedCampaign.donations_details.filter(
          (donation) => {
            return donation["status"] === "completed";
          }
        );
        const sentences = extractMissionStatement(
          tempLoadedNonprofit.mission_stmt,
          true
        );
        setNpStatement(sentences);
        setCampaign(tempLoadedCampaign);
        setNonprofit(tempLoadedNonprofit);
        setCompletedDonations(donations);
        return tempLoadedCampaign;
      } catch (error) {
        // Ignore abort errors - these are expected when component unmounts or effect re-runs
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        if (ifPreview) {
          setError(null);
        } else {
          setError(error);
        }
      }
    };
    if (!isOpen) {
      fetchAll();
    }

    return () => controller.abort();
  }, [nonprofit_name, animal_name, campaign_id, type, isOpen, ifPreview]);

  const handleDonationSubmit = async (event) => {
    event.preventDefault();

    let amountToDonate = selectedOption || parseFloat(customAmount);

    if (coverFee) {
      amountToDonate += parseFloat((amountToDonate * 0.03).toFixed(2));
    }

    setDonationAmount(amountToDonate);

    const initializePayment = async (campaign) => {
      if (!campaign || !campaign.donation_goal) return;

      const data = {
        campaign_id: campaign_id,
        campaign_type: campaign.campaign_type,
        amount: amountToDonate,
      };

      try {
        const response = await generateDonation(data);

        setClientSecret(response.client_secret);
        setDonationId(response.donation_id);
      } catch (error) {
        console.error("Error: ", error);
      }
    };

    initializePayment(campaign);
  };

  useEffect(() => {
    if (clientSecret) {
      const stripeInstance = window.Stripe(process.env.REACT_APP_STRIPE_KEY);
      const appearance = {
        /* appearance */
      };
      const options = {
        layout: {
          type: "tabs",
          defaultCollapsed: false,
        },
      };
      const elementsInstance = stripeInstance.elements({
        clientSecret,
        appearance,
      });
      const cardElement = elementsInstance.create("payment", options);
      cardElement.mount("#payment-element");

      cardElement.on("ready", () => {
        setIsStripeLoading(false);
      });

      setStripe(stripeInstance);
      setElements(elementsInstance);
    }
  }, [clientSecret]);

  // Shows blurred previews images
  const handlePreviewImageClick = (image) => {
    setBlurredPreviewImages((prevState) => ({
      ...prevState,
      [image.image]: true,
    }));
  };

  const handleSubmitPayment = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    const paymentElement = elements.getElement("payment");

    if (!paymentElement) {
      setPaymentResponse("Payment Element is not loaded.");
      return;
    }

    setIsStripeLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        setPaymentResponse(error.message);
      } else {
        if (paymentIntent && paymentIntent.status === "succeeded") {
          setPaymentResponse({ status: "success" });
          setShowModal(true);
          await donationSuccess({
            donation_id: donationId,
            campaign_type: campaign.campaign_type,
          });
        } else {
          setPaymentResponse({ error: error });
        }
      }
    } catch (error) {
      setPaymentResponse("An error occurred during payment.");
      console.error(error);
    } finally {
      setIsStripeLoading(false);
    }
  };

  const handleOptionClick = (amount) => {
    if (customAmount) {
      setCoverFee(false);
      setCustomAmount("");
    }

    if (selectedOption === amount) {
      setSelectedOption(null);
      setPrevAmount(0);
      setDonationAmount(0);
    } else {
      setSelectedOption(amount);
      setPrevAmount(amount);
      setDonationAmount(amount);
      setCoverFee(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setBlurredImages((prevState) => ({
      ...prevState,
      [imageUrl]: prevState[imageUrl] === false ? true : false,
    }));
  };

  const handleInputChange = (e) => {
    setPrevAmount(e.target.value);
    setCustomAmount(e.target.value);
    setDonationAmount(parseFloat(e.target.value));
    if (selectedOption) {
      setSelectedOption(null);
      setCoverFee(false);
    }
  };

  const handleCoverFeeChange = (e) => {
    setCoverFee(e.target.checked);
    const fee = parseFloat((prevAmount * 0.03).toFixed(2));
    if (e.target.checked) {
      setDonationAmount(Number(prevAmount) + Number(fee));
    } else {
      setDonationAmount(Number(prevAmount));
    }
  };

  let slideshowSettings = {
    dots: true,
    infinite: ifPreview
      ? (allImages.length > 0 || videoUrls.length > 0) &&
        allImages.length + videoUrls.length > 1
      : campaign && campaign.campaign_images
      ? campaign.campaign_images.length > 1
      : false,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    speed: 500,
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const closeModal = () => {
    setShowModal(false);
    window.location.reload();
  };

  if (error) {
    return <NotFound />;
  }

  const openLoginSignupModal = (type) => {
    setLoginOrSignup(type);
    setLoginSignupModalOpenModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLoginSignupModal = () => {
    setLoginSignupModalOpenModalOpen(false);
    document.body.style.overflow = "";
  };

  const handleFormSuccess = (success) => {
    if (success) {
      window.location.reload();
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();

    setCartError("");

    if (donationAmount > 0) {
      const cartData = {
        donation_id: campaign.id,
        amount: donationAmount,
        name: campaign.animal_name || campaign.campaign_title,
        nonprofit_name: campaign.nonprofit_name,
        image: campaign.campaign_images.find(
          (image) => image.graphic === false
        ),
        campaign_type: campaign.campaign_type,
        campaign_id: campaign.id,
        nonprofit_id: campaign.nonprofit,
      };

      try {
        // Update the global cart state
        dispatch(cartActions.addDonationToCart(cartData));

        // Open the success cart modal
        setShowCartModal(true);
      } catch (error) {
        setCartError("Unable to add to cart. Please try again.");
        console.error("Error adding item to cart:", error);
      }
    } else {
      setCartError("Please select or add an amount.");
      console.error("No donation amount selected.");
    }
  };

  const closeCartModal = () => {
    setShowCartModal(false);
  };

  if ((!campaign || !nonprofit) && !ifPreview) {
    return (
      <div className="flex flex-row ml-auto mt-[35vh] w-full h-full justify-center">
        <Spinner />
      </div>
    );
  }

  // Handles switching from 'Read more' to 'Read less'
  const handleReadMoreButton = () => {
    return ifReadMore ? setIfReadMore(false) : setIfReadMore(true);
  };

  const dataSource = campaignData || campaign;

  const isAnimalCampaign = campaignType === "Animal" || type === "animal";

  // Build inline pet attribute tags for animal campaigns
  const petAttributes = isAnimalCampaign
    ? [
        dataSource?.gender,
        dataSource?.breed,
        dataSource?.color,
        getKeyByValue(ageOptions, dataSource?.age),
      ].filter(Boolean)
    : [];

  return (
    <div
      className={`w-full h-full pb-8 ${
        ifPreview
          ? "relative main-div overflow-auto box-border"
          : "px-4 sm:px-8 lg:px-[132px]"
      }`}
    >
      <div
        className={`w-full h-full ${
          ifPreview
            ? "!fixed top-0 left-0 z-50 bg-white overflow-y-auto !m-0 pt-0 px-4 sm:px-16"
            : ""
        }`}
      >
        {/* Top section of Preview Campaign */}
        {ifPreview && (
          <section
            className={`md:flex-col md:h-96 flex lg:flex-row p-0 pb-4 w-full lg:h-16 mb-16`}
          >
            <h1
              className={`md:w-4/5 md:text-left md:ml-4 md:pt-4 md:pb-4 cursor-pointer lg:w-1/5 text-orange lg:p-0 lg:pb-4`}
              onClick={() => {
                onClose();
                setIfPreview(false);
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Exit Preview
            </h1>
            <div
              className={`flex items-center justify-center grow w-full bg-stone-400 text-white`}
            >
              <p>This is a preview of your campaign.</p>
            </div>
          </section>
        )}

        <div
          className={`flex flex-col w-full mt-24 gap-12 ${
            ifPreview ? "!mt-0" : ""
          }`}
        >
          {/* ── Section 1: About + Carousel ── */}
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-[136px] mt-12 lg:items-center">
            {/* Left: Title, attributes, description */}
            <div className="w-full lg:flex-1 flex flex-col">
              {ifPreview && (
                <h1 className="text-left !text-orange font-poppins !text-[50px]">
                  About {campaignData.animal_name}
                </h1>
              )}
              {campaign && (
                <h1 className="text-left !text-orange font-poppins !text-[50px]">
                  About {campaign.animal_name}
                </h1>
              )}

              {/* Inline pet attribute tags */}
              {petAttributes.length > 0 && (
                <p className="text-blue font-semibold mb-4 font-montserrat !text-[18px] text-left">
                  {petAttributes.join(" | ")}
                </p>
              )}

              {/* Campaign Description */}
              {(ifPreview || (campaign && campaign.description)) && (
                <div className="font-montserrat">
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      showFullDesc ? "max-h-none" : "max-h-[320px]"
                    }`}
                  >
                    {ifPreview ? (
                      campaignData?.description ? (
                        parseDescription === campaignData.description ? (
                          <p className="text-left !text-[16px]">{campaignData.description}</p>
                        ) : (
                          <div
                            className="text-left text-[16px]"
                            dangerouslySetInnerHTML={{
                              __html: parseDescription(campaignData.description),
                            }}
                          />
                        )
                      ) : (
                        <p>Description</p>
                      )
                    ) : parseDescription === campaign.description ? (
                      <p className="text-left">{campaign.description}</p>
                    ) : (
                      <div
                        className="text-left"
                        dangerouslySetInnerHTML={{
                          __html: parseDescription(campaign.description),
                        }}
                      />
                    )}
                  </div>
                  <div className="text-center mt-4">
                    <button
                      className="text-blue underline font-montserrat text-[16px]"
                      onClick={() => setShowFullDesc(!showFullDesc)}
                    >
                      {showFullDesc ? "Read Less" : "Read More"}
                    </button>
                  </div>
                </div>
              )}

              {/* Capital campaign details */}
              {(campaignType === "Capital" || type === "capital") && (
                <div className="text-left mt-4">
                  <p className="font-semibold">
                    Area:{" "}
                    <span className="font-normal">{dataSource?.land_area}</span>
                  </p>
                  <p className="font-semibold">
                    Location:{" "}
                    <span className="font-normal">
                      {dataSource?.land_location}
                    </span>
                  </p>
                  <p className="font-semibold">
                    Project Status:{" "}
                    <span className="font-normal">
                      {getKeyByValue(
                        projectStatusOptions,
                        dataSource?.project_status
                      )}
                    </span>
                  </p>
                  <p className="font-semibold">
                    Estimated Completion Date:{" "}
                    <span className="font-normal">
                      {formatDate(dataSource?.estimated_completion_date)}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Right: Image / Video Carousel */}
            <div className="w-full lg:w-[399px] lg:flex-shrink-0">
              {ifPreview ? (
                allImages.length > 0 || videoUrls.length > 0 ? (
                  <Slider
                    {...slideshowSettings}
                    className={`${classes.campaign_media_carousel} relative w-100 h-[40vh] lg:h-[404px] flex items-center justify-center`}
                  >
                    {allImages.map((img, index) => (
                      <div
                        key={index}
                        className="w-100 h-[40vh] lg:h-[404px] flex items-center justify-center relative"
                      >
                        <img
                          src={img.image}
                          alt={campaignData.animal_name}
                          className={`object-scale-down w-full h-full ${
                            img.graphic && !img.show
                              ? "cursor-pointer blur-md"
                              : ""
                          }`}
                        />
                        {img.graphic && !img.show && (
                          <PrimaryButton
                            onClick={() => handlePreviewImageClick(img)}
                            className="!absolute !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2"
                          >
                            Show
                          </PrimaryButton>
                        )}
                      </div>
                    ))}
                    {videoUrls.map((url, index) => (
                      <div
                        key={index}
                        className="w-100 h-[40vh] lg:h-[404px] flex items-center justify-center"
                      >
                        {url !== "URL not valid" ? (
                          <iframe
                            src={url}
                            title={`Success Video ${index + 1}`}
                            frameBorder="0"
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="object-scale-down w-full h-full"
                          />
                        ) : (
                          <p className="flex justify-center items-center w-full h-full text-red">
                            Invalid URL
                          </p>
                        )}
                      </div>
                    ))}
                  </Slider>
                ) : null
              ) : campaign?.campaign_images &&
                campaign.campaign_images.length > 0 ? (
                <Slider
                  {...slideshowSettings}
                  className={`${classes.campaign_media_carousel} relative w-100 h-[40vh] lg:h-[404px] flex items-center justify-center`}
                >
                  {campaign.campaign_images
                    .sort((a, b) => a.order - b.order)
                    .map((image, index) => (
                      <div
                        key={index}
                        className="w-100 h-[40vh] lg:h-[404px] flex items-center justify-center"
                      >
                        <div className="relative w-100 h-[40vh] lg:h-[404px]">
                          <img
                            src={image.image_url}
                            alt={campaign.animal_name}
                            className={`object-scale-down w-full h-full ${
                              image.graphic && blurredImages[image.image_url]
                                ? "cursor-pointer blur-md"
                                : ""
                            }`}
                          />
                          {image.graphic && blurredImages[image.image_url] && (
                            <PrimaryButton
                              onClick={() => handleImageClick(image.image_url)}
                              className="!absolute !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2"
                            >
                              Show
                            </PrimaryButton>
                          )}
                        </div>
                      </div>
                    ))}
                  {campaign.video_urls &&
                    campaign.video_urls.split(",").map((videoUrl, index) => (
                      <div
                        key={index}
                        className="w-100 h-[40vh] lg:h-[404px] flex items-center justify-center"
                      >
                        <iframe
                          src={videoUrl.trim()}
                          title={`Campaign Video ${index + 1}`}
                          frameBorder="0"
                          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="object-scale-down w-full h-full"
                        />
                      </div>
                    ))}
                </Slider>
              ) : null}
            </div>
          </div>

          {/* ── Section 2: Goal/Donate + Updates/Contributors ── */}
          <div className="flex flex-col lg:flex-row gap-8 lg:items-stretch">
            {/* Left: Combined progress + donate card */}
            <div className="w-full lg:w-[55%]">
              <div className="border rounded-xl p-6 shadow-lg w-full h-full">
                {/* Progress bar */}
                {ifPreview ? (
                  <DonationProgress campaign={campaignData} />
                ) : (
                  campaign && <DonationProgress campaign={campaign} />
                )}

                {/* Donation form */}
                {isLoginSignupModalOpen && (
                  <LoginSignupModal
                    loginOrSignup={loginOrSignup}
                    isOpen={openLoginSignupModal}
                    onClose={closeLoginSignupModal}
                    onFormSuccess={handleFormSuccess}
                  />
                )}

                {!ifPreview && paymentResponse?.status && showModal && (
                  <SuccessModal
                    message="Payment succeeded!"
                    onClose={closeModal}
                    showModal={showModal}
                  />
                )}

                <div className="w-full flex flex-col">
                  {!clientSecret ? (
                    <>
                      <div className="flex flex-row flex-wrap gap-2 mb-4">
                        {[10, 25, 35, 50, 75, 100].map((amount) => (
                          <button
                            key={amount}
                            className={`${
                              selectedOption === amount
                                ? "!bg-orange !text-white"
                                : "hover:bg-gray-200"
                            } border border-gray-400 !font-semibold text-black rounded-2xl py-2.5 px-4`}
                            type="button"
                            disabled={ifPreview}
                            onClick={
                              !ifPreview
                                ? () => handleOptionClick(amount)
                                : undefined
                            }
                          >
                            ${amount}
                          </button>
                        ))}
                      </div>
                      <form
                        id={classes.amount_form}
                        className="w-full no-underline"
                        onSubmit={!ifPreview ? handleDonationSubmit : undefined}
                      >
                        <input
                          type="number"
                          id="donation-amount"
                          name="donation-amount"
                          placeholder="Custom donation amount"
                          className="border border-gray-400 p-2 rounded-3xl w-full mb-2"
                          value={customAmount}
                          onChange={!ifPreview ? handleInputChange : undefined}
                          disabled={ifPreview}
                          min={1}
                          onWheel={(e) => e.target.blur()}
                        />
                        <div className="flex flex-row items-center gap-2 mb-4">
                          <label htmlFor="coverFee" className="text-[14px] text-grey-muted whitespace-nowrap">
                            Would you like to cover the transaction fee of ($
                            {(prevAmount * 0.03).toFixed(2)})?
                          </label>
                          <input
                            type="checkbox"
                            id="coverFee"
                            checked={!ifPreview ? coverFee : undefined}
                            onChange={
                              !ifPreview ? handleCoverFeeChange : undefined
                            }
                            disabled={ifPreview}
                            className="w-3 h-3 mt-[1px] flex-shrink-0"
                          />
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                          <PrimaryButton
                            type="button"
                            className="!w-36 !text-black"
                            onClick={handleAddToCart}
                          >
                            Add to Cart
                          </PrimaryButton>
                          <SecondaryButton
                            id="submit-amount"
                            type="submit"
                            className={`!w-36 !text-black ${
                              !ifPreview &&
                              !localUserData &&
                              "!bg-grey cursor-not-allowed"
                            }`}
                            disabled={!ifPreview && !localUserData}
                          >
                            Donate
                          </SecondaryButton>
                          {!ifPreview && !localUserData && (
                            <p>
                              <button
                                className="underline text-orange font-semibold"
                                onClick={() => openLoginSignupModal("signup")}
                              >
                                Sign Up
                              </button>{" "}
                              or{" "}
                              <button
                                className="underline text-blue font-semibold"
                                onClick={() => openLoginSignupModal("login")}
                              >
                                Login
                              </button>{" "}
                              to donate
                            </p>
                          )}
                        </div>
                        {cartError && (
                          <p className="text-error mt-2">{cartError}</p>
                        )}
                      </form>
                    </>
                  ) : (
                    <form
                      id="payment-form"
                      className={classes.payment_form}
                      onSubmit={handleSubmitPayment}
                    >
                      <div id="payment-element"></div>
                      <PrimaryButton type="submit" className="mt-4 mx-auto">
                        Complete Donation
                      </PrimaryButton>
                    </form>
                  )}

                  {showCartModal && (
                    <SuccessModal
                      message="Donation added to cart!"
                      onClose={closeCartModal}
                      showCartModal={showCartModal}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right: Recent Updates + Recent Contributors */}
            <div className="w-full lg:w-[45%] flex flex-col">
              {campaign && <CampaignDonorsBlock campaign={campaign} />}
            </div>
          </div>

          {/* ── Section 3: About Nonprofit + DonationBox ── */}
          <div className="flex flex-col lg:flex-row gap-8 lg:items-center">
            {/* Left: Nonprofit info */}
            {!ifPreview && (
              <div className="w-full lg:w-1/2 text-left">
                <p className="text-sm text-blue uppercase font-semibold tracking-widest mb-1">
                  About Us
                </p>
                {nonprofit && (
                  <>
                    <h2 className="!text-orange mb-1 font-poppins text-left !text-[30px] font-semibold">
                      About {nonprofit.org_name}
                    </h2>
                    {nonprofit.address?.city && (
                      <p className="text-gray-600 text-[12px] mb-4 text-left">
                        <FontAwesomeIcon icon={faLocationDot} className="mr-1" />
                        {nonprofit.address.city}
                        {nonprofit.address?.state
                          ? `, ${nonprofit.address.state}`
                          : ""}
                      </p>
                    )}
                  </>
                )}
                {npStatement.length > 3 ? (
                  <div className="flex flex-col text-left">
                    <p className="text-left">{ifReadMore ? npStatement : npStatement.slice(0, 3)}</p>
                    <button
                      className="text-left text-orange underline mt-4 w-fit"
                      onClick={handleReadMoreButton}
                    >
                      {ifReadMore ? "Read less" : "Read more..."}
                    </button>
                  </div>
                ) : (
                  <p className="text-left">{npStatement}</p>
                )}
                {nonprofit?.intro_video && (
                  <div className="!w-full !h-[50vh] border border-orange mt-8 mx-auto rounded-3xl flex items-center justify-center">
                    <iframe
                      src={nonprofit.intro_video}
                      title="Campaign Intro"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="object-scale-down w-full h-full rounded-3xl"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Right: DonationBox (Support Our Cause) */}
            <div className={`w-full lg:w-1/2 flex items-start justify-center${ifPreview ? " ml-auto" : ""}`}>
              <DonationBox nonprofit={nonprofit} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
